/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppNavParams } from '@euler/app/Routes';
import { Typography } from '@euler/app/components/typography';
import { BlurView } from '@euler/components/BlurView';
import { Center } from '@euler/components/Center';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isMobileBrowser,
  useIsMobileLayout,
} from '@euler/functions/responsiveness';
import { useDeviceType } from '@euler/functions/useDeviceType';
import { usePersistFn, useWatchValue } from '@euler/utils/hooks';
import { useTimer } from '@euler/utils/hooks/useTimer';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { DeviceType } from 'expo-device';
import { AnimatePresence, MotiView } from 'moti';
import { uniq } from 'ramda';
import {
  ReactNode,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  LayoutRectangle,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ViewShot from 'react-native-view-shot';
import { useTheme } from 'styled-components/native';
import { ImageAnnotationMetadata } from '../functions/AnnotationMetadata';
import { AnnotationMetadataBuilder } from '../functions/AnnotationMetadataBuilder';
import {
  AnnotationTagOption,
  annotationTagOptionToMetadata,
} from '../functions/AnnotationTagOption';
import { DefaultAnnotationTag } from '../functions/DefaultAnnotationTag';
import { AnnotationTagStyle, TagInfo } from '../functions/TagInfo';
import { annotationTagOptionStyle } from '../functions/annotationTagOptionStyle';
import {
  AnnotationTagColorStyle,
  DefaultAnnotationTagStyle,
  DefaultTagColorPresets,
  DefaultTagFontFamily,
  DefaultTagFontSize,
} from '../functions/tagPresets';
import { useTagAnnotations } from '../functions/useTagAnnotations';
import { AnnotationCanvas } from './AnnotationCanvas';
import { TagButtonBar } from './TagButtonBar';
import { TagColorPicker } from './TagColorPicker';
import { TagInteractionContext } from './TagView';

export type ImageAnnotationResult = {
  imageUri: string;
  originalImageUri: string | undefined;
  width: number;
  height: number;
  annotationMetadata: ImageAnnotationMetadata | undefined;
};

export type ImageAnnotationViewRef = {
  getAnnotationResult: () => Promise<ImageAnnotationResult | undefined>;
  hideControls: () => void;
};

type Props = {
  imageUri: string;
  mask?: { width: number; height: number };
  containerWidth: number;
  containerHeight: number;
  width: number;
  height: number;
  tags: string[];
  /**
   * the result options a tag can stand for; when more than one is given, a
   * popup menu lets the user switch the selected tag between them.
   */
  tagOptions?: AnnotationTagOption[];
  defaultTag?: DefaultAnnotationTag;
  quality?: number;
  annotationMetadata?: ImageAnnotationMetadata;
  watermark?: (scale?: number) => ReactNode;
  tagBarBlurred?: boolean;
  topOffset?: number;
  style?: StyleProp<ViewStyle>;
  tipStyle?: StyleProp<ViewStyle>;
  colorPickerStyle?: StyleProp<ViewStyle>;
  tagButtonBarStyle?: StyleProp<ViewStyle>;
};

export const ImageAnnotationView = memo(
  forwardRef<ImageAnnotationViewRef, Props>(
    (
      {
        containerWidth,
        containerHeight,
        width,
        height,
        imageUri,
        defaultTag,
        tags,
        tagOptions,
        watermark,
        quality,
        annotationMetadata,
        style,
        ...props
      },
      ref,
    ) => {
      return (
        <View
          css={`
            flex: 1;
            background-color: black;
          `}
          style={style}
        >
          <Center
            css={`
              flex: 1;
              align-items: center;
              justify-content: center;
            `}
          >
            <Content
              imageUri={imageUri}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
              width={width}
              height={height}
              quality={quality}
              predefinedTags={tags}
              tagOptions={tagOptions}
              defaultTag={defaultTag}
              watermark={watermark}
              annotationMetadata={annotationMetadata}
              {...props}
              ref={ref}
            />
          </Center>
        </View>
      );
    },
  ),
);

const getTagCoords = (tag: TagInfo) => {
  const [x1, y1] = [
    tag.x + (tag.translateX?.value ?? 0),
    tag.y + (tag.translateY?.value ?? 0),
  ];
  return {
    x1,
    y1,
    x2: x1 + tag.width,
    y2: y1 + tag.height,
  };
};

const Content = memo(
  forwardRef<
    ImageAnnotationViewRef,
    {
      imageUri: string;
      width: number;
      height: number;
      containerWidth: number;
      containerHeight: number;
      predefinedTags: string[];
      tagOptions?: AnnotationTagOption[];
      defaultTag?: DefaultAnnotationTag;
      quality?: number;
      topOffset?: number;
      annotationMetadata?: ImageAnnotationMetadata;
      watermark?: (scale?: number) => ReactNode;
      tipStyle?: StyleProp<ViewStyle>;
      tagBarBlurred?: boolean;
      colorPickerStyle?: StyleProp<ViewStyle>;
      tagButtonBarStyle?: StyleProp<ViewStyle>;
    }
  >(
    (
      {
        imageUri,
        width,
        height,
        containerHeight,
        topOffset = 0,
        tipStyle,
        colorPickerStyle,
        tagButtonBarStyle,
        watermark,
        defaultTag,
        predefinedTags,
        tagOptions,
        quality = 0.85,
        tagBarBlurred,
        ...props
      },
      ref,
    ) => {
      const insets = useSafeAreaInsets();
      const isMobile = useIsMobileLayout();
      const { rpx } = useResponsiveContext();
      const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
      const [isControlsVisible, setIsControlsVisible] = useState(true);
      const viewShot = useRef<ViewShot>(null);
      const isTablet = useDeviceType() === DeviceType.TABLET;
      const { scale: pixelRatio } = useWindowDimensions();
      const theme = useTheme();

      const {
        tags,
        addNewTag,
        updateTag,
        initTagInteractionContext,
        bringTagToFront,
        selectTag,
        deselectAll,
        deleteTag,
        selectedTag,
        updateTagStyle,
      } = useTagAnnotations(props.annotationMetadata);

      const [nextTag, setNextTag] = useState<{
        name: string;
        style?: AnnotationTagStyle;
      }>();

      const tipTimer = useTimer();

      const onTagButtonPress = usePersistFn((name: string) => {
        setNextTag({ name });
        deselectAll();
        tipTimer.schedule(() => {
          setNextTag(undefined);
        }, 60000);
      });

      const onAddTag = useCallback(() => {
        setIsControlsVisible(false);
        navigation.navigate('_addCustomTagAnnotation', {
          onCancel: () => {
            navigation.goBack();
            setIsControlsVisible(true);
          },
          onDone: (name, style) => {
            navigation.goBack();
            setIsControlsVisible(true);
            name = name.trim();
            if (!name) return;
            setNextTag({ name, style });
          },
        });
      }, [navigation]);

      const onCanvasTap = usePersistFn((x: number, y: number) => {
        deselectAll();

        if (!nextTag) return;

        setNextTag(undefined);

        tipTimer.clear();

        addNewTag({ name: nextTag.name, x, y, style: nextTag.style });
      });

      const onSelectTagOption = usePersistFn(
        (id: string, option: AnnotationTagOption) => {
          updateTag(id, {
            name: option.label,
            option,
            style: annotationTagOptionStyle(theme, option),
          });
        },
      );

      const onTagReadyForInteraction = useCallback(
        (id: string, interaction: TagInteractionContext) => {
          initTagInteractionContext(id, interaction);
        },
        [initTagInteractionContext],
      );

      const onTagMoveBegin = useCallback(
        (id: string) => {
          bringTagToFront(id);
        },
        [bringTagToFront],
      );

      const onTagLayoutChange = useCallback(
        (id: string, layout: LayoutRectangle) => {
          updateTag(id, layout, true);
        },
        [updateTag],
      );

      const onTagPress = useCallback(
        (id: string) => {
          selectTag(id);
        },
        [selectTag],
      );

      const onDeleteTag = useCallback(
        (id: string) => {
          deleteTag(id);
        },
        [deleteTag],
      );

      const onTagColorStyleChange = useCallback(
        (style: AnnotationTagColorStyle) => {
          if (selectedTag == null) return;
          updateTagStyle(selectedTag.id, style);
        },
        [selectedTag, updateTagStyle],
      );

      const isDefaultTagAdded = useRef(false);
      useLayoutEffect(() => {
        const name = defaultTag
          ? typeof defaultTag === 'string'
            ? defaultTag
            : defaultTag.name
          : undefined;
        if (!name || isDefaultTagAdded.current) return;
        const { color, option } =
          defaultTag && typeof defaultTag !== 'string'
            ? defaultTag
            : { color: undefined, option: undefined };
        // an option-backed tag is always painted with the color of the
        // defective level its option resolves to.
        const style = option
          ? annotationTagOptionStyle(theme, option)
          : color
          ? DefaultTagColorPresets[color]
          : undefined;
        addNewTag({ name, x: width / 2, y: height / 2, style, option });
        isDefaultTagAdded.current = true;
      }, [defaultTag, width, height, addNewTag, theme]);

      const getAnnotationResult = usePersistFn(async () => {
        const annotationMetadata = !tags.length
          ? undefined
          : AnnotationMetadataBuilder.builder({
              keywords: uniq(tags.map(x => x.name)),
              canvasWidth: width,
              canvasHeight: height,
            })
              .withStyle('tag', {
                stroke: DefaultAnnotationTagStyle.stroke,
                fill: DefaultAnnotationTagStyle.fill,
              })
              .withStyle('text', {
                fill: DefaultAnnotationTagStyle.textFill,
              })
              .withObjects(objects => {
                tags.forEach(tag =>
                  objects.tag(t =>
                    t
                      .withCoords(getTagCoords(tag))
                      .withStyleName('tag')
                      .withZIndex(tag.zIndex?.value)
                      .withOption(
                        tag.option
                          ? annotationTagOptionToMetadata(tag.option)
                          : undefined,
                      )
                      .withStyle(style =>
                        style
                          .withFill(tag.style?.fill)
                          .withStroke(tag.style?.stroke),
                      )
                      .withName(
                        tag.name,
                        'text',
                        {
                          family: DefaultTagFontFamily,
                          size: DefaultTagFontSize,
                        },
                        style => style.withFill(tag.style?.textFill),
                      ),
                  ),
                );
              })
              .getMetadata();

        let snapshotImageUri = imageUri;
        const originalImageUri = imageUri;

        if (viewShot.current) {
          try {
            const result = await viewShot.current.capture?.();
            if (result) {
              snapshotImageUri = result;
              if (!snapshotImageUri.startsWith('file://')) {
                snapshotImageUri = `file://${snapshotImageUri}`;
              }
            }
          } catch (e) {
            console.error(e);
          }
        }

        return {
          imageUri: snapshotImageUri,
          originalImageUri,
          width,
          height,
          annotationMetadata,
        };
      });

      const pendingResult = useRef<(result: ImageAnnotationResult) => void>();

      useWatchValue(selectedTag, async (prev, curr) => {
        if (prev && !curr && pendingResult.current) {
          const result = await getAnnotationResult();
          pendingResult.current(result);
          pendingResult.current = undefined;
        }
      });

      useImperativeHandle(ref, () => ({
        getAnnotationResult: async () => {
          if (selectedTag) {
            // clear the selection otherwise the selected view artifacts
            // will also be rendered.
            return await new Promise(resolve => {
              pendingResult.current = resolve;
              deselectAll();
            });
          }
          return await getAnnotationResult();
        },
        hideControls: () => {
          setIsControlsVisible(false);
        },
      }));

      const scale = isTablet ? Math.min(width, height) / 428 : 1;

      const controlsPlacement =
        containerHeight < height ||
        height / containerHeight > 0.8 ||
        (isMobile && isMobileBrowser())
          ? 'inside'
          : 'outside';

      return (
        <>
          {/** tapping anywhere off the image clears the selection, and with it
               the option menu of the selected tag. it sits below the image so
               that taps on the image itself still reach the canvas. */}
          {selectedTag != null ? (
            <Pressable style={StyleSheet.absoluteFill} onPress={deselectAll} />
          ) : null}

          <ViewShot
            ref={viewShot}
            options={{
              format: 'jpg',
              quality,
              width: isTablet
                ? width / pixelRatio
                : width / Math.min(pixelRatio, 1.25),
              height: isTablet
                ? height / pixelRatio
                : height / Math.min(pixelRatio, 1.25),
            }}
            css={`
              width: ${width}px;
              height: ${height}px;
            `}
          >
            <Img
              uri={imageUri}
              css={`
                width: ${width}px;
                height: ${height}px;
              `}
              resizeMode="cover"
            />
            {watermark != null && (
              <View
                css={`
                  position: absolute;
                  right: 0;
                  top: 0;
                `}
              >
                {watermark(scale)}
              </View>
            )}
            <AnnotationCanvas
              width={width}
              height={height}
              scale={scale}
              tags={tags}
              tagOptions={tagOptions}
              onTap={onCanvasTap}
              onTagMoveBegin={onTagMoveBegin}
              onTagReadyForInteraction={onTagReadyForInteraction}
              onTagLayoutChange={onTagLayoutChange}
              onTagPress={onTagPress}
              onSelectTagOption={onSelectTagOption}
              onDeleteTag={onDeleteTag}
            />
          </ViewShot>

          <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
            <View
              pointerEvents="box-none"
              style={
                controlsPlacement === 'inside'
                  ? {
                      paddingTop:
                        64 +
                        Math.max(0, (containerHeight - height) / 2) +
                        topOffset,
                    }
                  : {
                      height:
                        Math.max(0, (containerHeight - height) / 2) + topOffset,
                      justifyContent: 'flex-end',
                    }
              }
            >
              {/** color pickers */}
              <AnimatePresence>
                {selectedTag != null && (
                  <MotiView
                    from={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'timing', duration: 400 }}
                    exitTransition={{ type: 'timing', duration: 300 }}
                    css={`
                      margin-bottom: ${rpx(20)}px;
                    `}
                  >
                    <TagColorPicker
                      selectedColor={
                        selectedTag?.style?.fill ??
                        DefaultAnnotationTagStyle.fill
                      }
                      onChange={onTagColorStyleChange}
                      style={colorPickerStyle}
                    />
                  </MotiView>
                )}
              </AnimatePresence>

              {/** tap placement tip */}
              <AnimatePresence>
                {nextTag != null && (
                  <MotiView
                    from={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'timing', duration: 400 }}
                    exitTransition={{ type: 'timing', duration: 300 }}
                    css={`
                      align-items: center;
                      justify-content: center;
                      margin-bottom: ${rpx(20)}px;
                    `}
                    style={tipStyle}
                  >
                    <Typography.Label light color="#fff" size={rpx(14)}>
                      轻触图片添加该故障标签
                    </Typography.Label>
                  </MotiView>
                )}
              </AnimatePresence>
            </View>

            <AnimatePresence>
              {isControlsVisible !== false && (
                <MotiView
                  nativeID="tag-bar"
                  from={{ translateY: rpx(50) + insets.top, opacity: 0 }}
                  animate={{ translateY: 0, opacity: 1 }}
                  exit={{ translateY: rpx(50) + insets.top, opacity: 0 }}
                  transition={{
                    type: 'timing',
                  }}
                  css={`
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    right: 0;
                    justify-content: center;
                    background-color: ${tagBarBlurred ? 'transparent' : '#000'};
                  `}
                  style={[
                    Platform.OS === 'web'
                      ? {
                          height: 90,
                        }
                      : undefined,
                    tagButtonBarStyle,
                  ]}
                >
                  {tagBarBlurred ? (
                    <BlurView blurType="extraDark" blurAmount={10}>
                      <TagButtonBar
                        tags={predefinedTags}
                        activeTag={nextTag?.name}
                        onTagPress={onTagButtonPress}
                        onAddTag={onAddTag}
                        css={`
                          padding-bottom: ${insets.bottom + rpx(15)}px;
                        `}
                      />
                    </BlurView>
                  ) : (
                    <TagButtonBar
                      tags={predefinedTags}
                      activeTag={nextTag?.name}
                      onTagPress={onTagButtonPress}
                      onAddTag={onAddTag}
                      css={`
                        padding-bottom: ${insets.bottom + rpx(15)}px;
                      `}
                    />
                  )}
                </MotiView>
              )}
            </AnimatePresence>
          </View>
        </>
      );
    },
  ),
);
